// Copyright (c) 2020 by Audere
//
// Use of this source code is governed by an LGPL-3.0 license that
// can be found in the LICENSE file distributed with this file.

import {
  NewTestLogsMetadata,
  createNewTestRun,
  logNewTestRunMetadata,
} from "store/testrun";
import { ROUTE_DEFINITIONS, getPath } from "routes/routes";
import React, { useContext, useEffect, useState } from "react";

import { CustomerContext } from "components/App/DashboardApp";
import { Redirect } from "react-router-dom";
import { UNSET_USER_ID } from "./TestRunConstants";
import { formatISO } from "date-fns";
import { useAppConfig } from "store/config";
import { useAppDispatch } from "store";
import { useAuthState } from "../../store/auth";
import { useTestRunConfig } from "./useTestRunConfig";

const getNewTestRunLogsMetadata = (config: {
  testRunUID: string;
  userUID: string;
  appName: string | undefined;
}): NewTestLogsMetadata => {
  const { testRunUID, userUID, appName } = config;
  const location = document.location;
  const referrer = document.referrer;
  const href = location.href;
  const KIT_TOKENS = ["home1", "swab1"];
  const token: string | undefined = KIT_TOKENS.find(
    token => href.indexOf(token) >= 0
  );
  let userAgent: string = "";
  try {
    userAgent = navigator.userAgent;
  } catch (exception) {
    // do nothing
  }

  const now = new Date();
  const UTCTime = now.toISOString();
  const LocalTime = formatISO(now);
  const origin =
    location.origin ||
    location.protocol +
      "//" +
      location.hostname +
      (location.port ? ":" + location.port : "");
  return {
    appName: appName || "",
    href: href,
    origin: origin,
    referrer: referrer,
    token: token || "",
    TestRunUID: testRunUID,
    UserUID: userUID,
    UTCTime,
    LocalTime,
    userAgent,
  };
};

export default () => {
  const { auth } = useAuthState();
  const config = useAppConfig();
  const customerConfig = useContext(CustomerContext);
  const appName = customerConfig?.name;
  const { getStartStep } = useTestRunConfig();
  const [testRunUID, setTestRunUID] = useState<string | null>(null);
  const dispatch = useAppDispatch();
  const authUID = auth?.uid;

  useEffect(() => {
    const newTestRun = async () => {
      const userUID =
        !authUID && customerConfig.allowGuestRuns ? UNSET_USER_ID : authUID;

      if (userUID === undefined) {
        throw new Error("Something went wrong");
      }

      const newTestRunUID = await dispatch(
        createNewTestRun({
          orgId: customerConfig.id,
          userUID: userUID,
          startStep: getStartStep(),
        })
      );

      const logs = getNewTestRunLogsMetadata({
        testRunUID: newTestRunUID,
        userUID,
        appName,
      });
      setTestRunUID(newTestRunUID);

      dispatch(logNewTestRunMetadata(logs));
    };
    !testRunUID && newTestRun();
  }, [
    config,
    authUID,
    customerConfig.id,
    getStartStep,
    appName,
    dispatch,
    testRunUID,
    customerConfig.allowGuestRuns,
  ]);

  return testRunUID ? (
    <Redirect
      to={{
        pathname: getPath(ROUTE_DEFINITIONS["PERFORMTEST"], {
          params: {
            ":testRunUID": testRunUID,
            ":step": getStartStep(),
          },
        }),
      }}
    />
  ) : (
    <div />
  );
};
